import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchWarehouseStatusThunk } from "../../store/reports/thunk";
import { setDate } from "../../store/reports/actions";
import "../../styles/global.css";
import noDataImage from "../../assets/no_data.png";
import {
  Home,
  PauseCircle,
  PlayCircle,
  ChevronLeftCircle,
  ChevronRightCircle,
} from "lucide-react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { APP_CONSTANTS } from "../../utilities/app_constants";
import { fetchOrganizationThunk } from "../../store/redux/thunk";

const WarehouseStatus = ({
  reportData,
  onNext,
  onPrevious,
  onPause,
  isPaused,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const [params, setParams] = useState({});
  const [organizationName, setOrganizationName] = useState("");

  // Fetch user ID from AsyncStorage
  useEffect(() => {
    const fetchUserId = async () => {
      const userId = await AsyncStorage.getItem("userId");
      setUserId(userId);
    };
    fetchUserId();
  }, []);

  useEffect(() => {
    if (userId) {
      dispatch(fetchOrganizationThunk(userId));
    }
  }, [dispatch, userId]);

  const { warehouseStatusData, currentDate, loading, error } = useSelector(
    (state) => state.report
  );

  const { organizations } = useSelector((state) => state.filter);
  console.log(organizations);

  // Extract orgId from reportData URL
  useEffect(() => {
    if (reportData?.url) {
      const urlParams = new URLSearchParams(reportData.url.split("?")[1]);
      const extractedParams = {
        orgId: urlParams.get("AD_Org_ID"),
        note: urlParams.get("Note"),
      };
      setParams(extractedParams);
    }
  }, [reportData]);

  // Find and set organization name when orgId is available
  useEffect(() => {
    if (params.orgId && organizations?.length > 0) {
      const matchingOrg = organizations.find((org) => {
        return org?.Ad_Org_ID && org.Ad_Org_ID.toString() === params.orgId;
      });
      if (matchingOrg) {
        setOrganizationName(matchingOrg.Organization || "Unknown Organization");
      } else {
        setOrganizationName("Unknown Organization");
      }
    }
  }, [params.orgId, organizations]);

  const getCurrentDate = () => {
    return new Date().toISOString().split("T")[0]; // Returns YYYY-MM-DD
  };

  // Fetch warehouse data when orgId is available
  useEffect(() => {
    if (params.orgId) {
      const date = getCurrentDate();
      dispatch(setDate(date));
      dispatch(fetchWarehouseStatusThunk(params.orgId, date, date));
    }
  }, [dispatch, params]);

  // Auto-Navigate after 1 minute if not paused
  useEffect(() => {
    if (!isPaused) {
      // Convert interval to an integer and check for invalid values
      const intervalInSeconds = parseInt(reportData?.interval, 10);

      // Fallback to constant time if interval is invalid, empty, or zero
      const displayTime =
        !isNaN(intervalInSeconds) && intervalInSeconds > 0
          ? intervalInSeconds * 1000 // Convert seconds to milliseconds
          : APP_CONSTANTS.REPORT_DISPLAY_TIME;

      const timeout = setTimeout(() => {
        onNext();
      }, displayTime);

      return () => clearTimeout(timeout);
    }
  }, [onNext, isPaused, reportData?.interval]);

  const warehouseStatusColumnMappings = {
    processingqty: "Processing",
    blendingqty: "Blending",
    packingqty: "Packing",
    processingpackingqty: "Processing & Packing",
    repackingqty: "Repacking",
    bulkingqty: "Bulking",
    treatment: "Treatment",
    reprocessingqty: "Reprocessing",
    remantrocessingqty: "Reman Processing",
  };

  // Responsive styles only - UI same राहील
  useEffect(() => {
    const styleSheet = document.createElement("style");

    styleSheet.textContent = `
      /* ONLY RESPONSIVE LAYOUT CHANGES - NO UI/COLOR CHANGES */
      
      /* Container responsiveness */
      .container {
        max-width: 100%;
        overflow-x: hidden;
      }
      
      /* Header responsive layout */
      @media (max-width: 1024px) {
        .header {
          flex-wrap: wrap !important;
          justify-content: center !important;
        }
        
        .header .home-button {
          order: 1;
        }
        
        .header .controls {
          order: 2;
        }
        
        .header h1 {
          order: 3;
          width: 100%;
          text-align: center;
          margin: 10px 0 !important;
        }
        
        .header .header-info {
          order: 4;
          width: 100%;
          justify-content: center !important;
          margin: 8px 0 !important;
        }
        
        .header .date-display {
          order: 5;
          margin: 8px auto !important;
        }
        
        .header .logo-container {
          order: 6;
          margin: 5px 0 !important;
        }
      }
      
      @media (max-width: 768px) {
        .header .controls svg {
          width: 32px !important;
          height: 32px !important;
        }
        
        .header h1 {
          font-size: 18px !important;
        }
        
        .current-date {
          width: 70px !important;
          font-size: 11px !important;
        }
        
        .header-info span {
          font-size: 12px !important;
        }
      }
      
      @media (max-width: 480px) {
        .header .controls {
          gap: 5px !important;
        }
        
        .header .controls svg {
          width: 28px !important;
          height: 28px !important;
        }
        
        .header h1 {
          font-size: 16px !important;
        }
        
        .current-date {
          width: 60px !important;
          font-size: 10px !important;
        }
        
        .header-info span {
          font-size: 10px !important;
        }
      }
      
      /* Table responsiveness */
      .responsive-table-wrapper {
        width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }
      
      .warehouse-table {
        min-width: 800px;
      }
      
      /* Responsive table cell styles */
      @media (max-width: 768px) {
        .warehouse-table th,
        .warehouse-table td {
          font-size: 11px !important;
          padding: 6px !important;
          white-space: nowrap;
        }
      }
      
      @media (max-width: 480px) {
        .warehouse-table th,
        .warehouse-table td {
          font-size: 9px !important;
          padding: 4px !important;
        }
      }
      
      /* Marquee responsiveness */
      @media (max-width: 768px) {
        .marquee-container {
          font-size: 14px !important;
          bottom: 20px !important;
        }
        
        .marquee-container div span {
          padding-right: 60px !important;
        }
      }
      
      @media (max-width: 480px) {
        .marquee-container {
          font-size: 11px !important;
          bottom: 10px !important;
          padding: 8px 0 !important;
        }
        
        .marquee-container div span {
          padding-right: 40px !important;
        }
      }
      
      /* Keep ALL original table styles exactly as they were */
      .responsive-table-wrapper {
        width: 100%;
        overflow-x: auto;
      }
      
      .warehouse-table {
        width: 100%;
        border-collapse: collapse;
      }
      
      .loading, .error {
        text-align: center;
        padding: 40px;
        font-size: 18px;
      }
      
      .no-data {
        text-align: center;
        padding: 40px;
      }
    `;

    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Scrolling text content
  const scrollingText = `${params?.note || ""}`;

  return (
    <div className="container" style={{ position: "relative", minHeight: "100vh" }}>
      <header className="header" style={{ flexWrap: "wrap", gap: "1px", display: "flex", alignItems: "center" }}>
        <button
          className="home-button"
          onClick={() =>
            navigate("/menu", {
              state: { hideModal: true },
            })
          }
        >
          <Home
            className="home-icon"
            size={120}
            style={{
              width: "35px",
              height: "40px",
            }}
          />
        </button>
        
        {/* Control Buttons */}
        <div className="controls" style={{ display: "flex", gap: "1px", alignItems: "center" }}>
          <ChevronLeftCircle
            size={40}
            onClick={onPrevious}
            style={{ cursor: "pointer" }}
          />
          {isPaused ? (
            <PlayCircle
              size={40}
              onClick={() => onPause(false)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <PauseCircle
              size={40}
              onClick={() => onPause(true)}
              style={{ cursor: "pointer" }}
            />
          )}
          <ChevronRightCircle
            size={40}
            onClick={onNext}
            style={{ cursor: "pointer" }}
          />
        </div>
        
        <h1 style={{ fontSize: "clamp(16px, 4vw, 28px)", margin: "5px 0" }}>Warehouse Status</h1>
        
        <div className="header-info">
          <span
            style={{
              color: " #0066cc",
              fontSize: "clamp(16px, 3vw, 28px)",
              fontWeight: "bold",
            }}
          >
            <span
              style={{
                background: "linear-gradient(90deg, #0A2A88, #1565C0)",
                color: "#FFFFFF",
                padding: "6px 18px",
                borderRadius: "30px",
                fontSize: "clamp(14px, 2vw, 28px)",
                fontWeight: "800",
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                border: "2px solid rgba(255,255,255,0.7)",
                letterSpacing: "0.5px",
              }}
            >
              <span
                style={{
                  color: "#BBDEFB",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  opacity: 0.9,
                }}
              >
                Organization
              </span>

              <span
                style={{
                  width: "2px",
                  height: "22px",
                  background: "rgba(255,255,255,0.5)",
                }}
              />

              <span
                style={{
                  color: "#FFFFFF",
                  wordBreak: "break-word",
                  maxWidth: '400px',
                  fontWeight: "900",
                  whiteSpace: "wrap",
                }}
              >
                {organizationName || "-"}
              </span>
            </span>
          </span>
        </div>
        
        <div
          className="date-display"
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <span>Current Date:</span>
          <input
            type="text"
            value={currentDate}
            readOnly
            className="current-date"
            style={{
              width: "80px",
              minWidth: "80px",
              padding: "4px 8px",
              fontSize: "14px",
            }}
          />
        </div>
        
        <div className="logo-container">
          <a
            href="https://www.digiopenservices.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Logo.png"
              alt="Logo"
              className="header-logo"
              style={{
                maxwidth: "110px",
                height: "40px",
                objectFit: "contain",
                borderRadius: "6px",
                margin: "2px 10px",
                cursor: "pointer",
              }}
            />
          </a>
        </div>
      </header>

      {loading && <p className="loading">Loading data, please wait...</p>}
      {error && <p className="error">{error}</p>}
      {!loading && warehouseStatusData && warehouseStatusData.length === 0 && (
        <div className="no-data">
          <img
            src={noDataImage}
            alt="No data available"
            className="no-data-image"
          />
          <p>No data available for the current date.</p>
        </div>
      )}

      <div className="table-layout">
        <div className="table-column">
          {/* Warehouse Status Table */}
          {!loading &&
            warehouseStatusData &&
            warehouseStatusData.length > 0 && (
              <div className="table-container responsive-table-wrapper">
                <table className="data-table warehouse-table">
                  <thead>
                    <tr>
                      <th className="header-yellow" style={{ minWidth: "180px" }}>Linewise Performance</th>
                      {warehouseStatusData.map((item, index) => (
                        <th key={index} className="header-green" style={{ minWidth: "100px" }}>
                          {item.lineno}
                        </th>
                      ))}
                      <th className="header-total" style={{ minWidth: "80px" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(warehouseStatusColumnMappings).map(
                      (key, rowIndex) => {
                        const rowTotal = warehouseStatusData.reduce(
                          (sum, item) => sum + parseFloat(item[key] || 0),
                          0
                        );

                        return (
                          <tr key={rowIndex}>
                            <th className="activity-column" style={{ minWidth: "180px" }}>
                              {warehouseStatusColumnMappings[key]}
                            </th>
                            {warehouseStatusData.map((item, colIndex) => (
                              <td key={colIndex} style={{ minWidth: "100px" }}>
                                {item[key] !== "0" ? item[key] : "0"}
                              </td>
                            ))}
                            <td className="total-column" style={{ minWidth: "80px" }}>
                              {rowTotal !== 0 ? rowTotal.toFixed(3) : "0"}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>
  <div
        style={{
          position: "fixed",
          bottom: "40px",
          left: 0,
          right: 0,
          color: "#022fe3",
          padding: "12px 0",
          overflow: "hidden",
          whiteSpace: "nowrap",
          fontFamily: "monospace",
          fontSize: "25px",
          fontWeight: "700",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            display: "inline-block",
            animation: "scrollRightToLeft 35s linear infinite",
            whiteSpace: "nowrap",
            paddingLeft: "100%",
          }}
        >
          <span>{scrollingText}</span>
        </div>

        <style>
          {`
      @keyframes scrollRightToLeft {
        0% {
          transform: translateX(0%);
        }
        100% {
          transform: translateX(-100%);
        }
      }
    `}
        </style>
      </div>
    </div>
  );
};

export default WarehouseStatus;